import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { drawScene, SceneFrameInput, SceneLabels } from './drawScene';
import { useSceneAnchors } from './sceneRegistry';
import { LAB_NAV_HEIGHT } from '../constants';

/**
 * Fixed full-viewport canvas behind the landing content. Runs the 5a scene at
 * the display's refresh rate; with prefers-reduced-motion it renders a static
 * frame that only updates on scroll/resize (time is frozen, so nothing moves
 * on its own).
 */
const SceneCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { anchors } = useSceneAnchors();
  const { t } = useTranslation();

  // Live label ref so a language switch reaches the running loop without restarting it
  const labelsRef = useRef<SceneLabels>({} as SceneLabels);
  labelsRef.current = {
    asr: t('lab.scene.asr'),
    tts: t('lab.scene.tts'),
    slm: t('lab.scene.slm'),
    intelligence: t('lab.scene.intelligence'),
    sentUser: t('lab.scene.sentUser'),
    sentAssistant: t('lab.scene.sentAssistant'),
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const eye = { x: 0, y: 0, dist: 0, has: false };
    let raf = 0;
    const start = performance.now();

    const onMouseMove = (e: MouseEvent) => {
      if (eye.has) eye.dist += Math.hypot(e.clientX - eye.x, e.clientY - eye.y);
      eye.x = e.clientX;
      eye.y = e.clientY;
      eye.has = true;
    };

    const render = (now: number) => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';

      const scrollY = window.scrollY || 0;
      const doc = document.documentElement;
      const frame: SceneFrameInput = {
        t: reducedMotion ? 0 : (now - start) / 1000,
        scrollY,
        w,
        h,
        docMax: Math.max(1, doc.scrollHeight - window.innerHeight),
        navH: LAB_NAV_HEIGHT,
        compact: w < 768,
        anchors: {},
        labels: labelsRef.current,
        eyeDist: eye.dist,
        rtl: document.documentElement.dir === 'rtl',
      };
      anchors.current.forEach((el, key) => {
        const r = el.getBoundingClientRect();
        frame.anchors[key] = { top: r.top + scrollY, pin: Math.max(0, r.height - h) };
      });
      drawScene(ctx, frame);
    };

    if (reducedMotion) {
      // Static mode: redraw only when layout or scroll position changes
      let queued = false;
      const requestStaticFrame = () => {
        if (queued) return;
        queued = true;
        raf = requestAnimationFrame((now) => {
          queued = false;
          render(now);
        });
      };
      requestStaticFrame();
      window.addEventListener('scroll', requestStaticFrame, { passive: true });
      window.addEventListener('resize', requestStaticFrame);
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('scroll', requestStaticFrame);
        window.removeEventListener('resize', requestStaticFrame);
      };
    }

    const loop = (now: number) => {
      render(now);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [anchors]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 h-full w-full pointer-events-none"
      aria-hidden="true"
    />
  );
};

export default SceneCanvas;
